import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  Dumbbell, Users, Swords, Flag, Map, GraduationCap, MessageSquare,
  Search, MapPin, CalendarDays,
} from "lucide-react";
import { IMG, loginTo } from "./data";

const SEARCH_TABS = [
  { key: "grounds", label: "Grounds", icon: Dumbbell, dest: "/app/facilities", cta: "Search Grounds", fields: [{ k: "city", ph: "City or area", icon: MapPin }, { k: "sport", ph: "Sport", icon: Dumbbell }, { k: "date", ph: "Date", icon: CalendarDays, type: "date" }] },
  { key: "teams", label: "Teams", icon: Users, dest: "/app/teams", cta: "Find Teams", fields: [{ k: "sport", ph: "Sport", icon: Dumbbell }, { k: "city", ph: "City", icon: MapPin }] },
  { key: "matches", label: "Matches", icon: Swords, dest: "/app/matches", cta: "Find Matches", fields: [{ k: "sport", ph: "Sport", icon: Dumbbell }, { k: "city", ph: "City", icon: MapPin }] },
  { key: "events", label: "Events", icon: Flag, dest: "/app/enquiries", cta: "Explore Events", fields: [{ k: "sport", ph: "Sport / event", icon: Flag }, { k: "city", ph: "City", icon: MapPin }] },
  { key: "tourism", label: "Sports Tourism", icon: Map, dest: "/app/enquiries", cta: "Explore Trips", fields: [{ k: "destination", ph: "Destination", icon: MapPin }, { k: "date", ph: "Date", icon: CalendarDays, type: "date" }] },
  { key: "academies", label: "Academies", icon: GraduationCap, dest: "/app/discover", cta: "Find Academies", fields: [{ k: "sport", ph: "Sport", icon: Dumbbell }, { k: "city", ph: "City", icon: MapPin }] },
  { key: "community", label: "Community", icon: MessageSquare, dest: "/app/community", cta: "Join Community", fields: [] },
];

function SearchWidget() {
  const [active, setActive] = useState("grounds");
  const [vals, setVals] = useState({});
  const [opts, setOpts] = useState({ cities: [], sports: [] });
  useEffect(() => {
    api.get("/public/filters").then((r) => setOpts({ cities: r.data?.cities || [], sports: r.data?.sports || [] })).catch(() => {});
  }, []);
  const tab = SEARCH_TABS.find((t) => t.key === active);
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(vals).filter(([, v]) => v))).toString();
  const to = loginTo(tab.dest + (qs ? `?${qs}` : ""));
  const listFor = (k) => (k === "city" || k === "destination" ? "dl-cities" : k === "sport" ? "dl-sports" : undefined);

  return (
    <div data-testid="search-widget" className="w-full min-w-0 rounded-2xl border border-border bg-card p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)]">
      <datalist id="dl-cities">{opts.cities.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="dl-sports">{opts.sports.map((s) => <option key={s} value={s} />)}</datalist>
      <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-border pb-2">
        {SEARCH_TABS.map((t) => {
          const I = t.icon; const on = t.key === active;
          return (
            <button key={t.key} data-testid={`search-tab-${t.key}`} onClick={() => { setActive(t.key); setVals({}); }}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
              <I className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex min-w-0 flex-col gap-2 p-3 sm:flex-row sm:items-end">
        {tab.fields.length ? tab.fields.map((f) => {
          const FI = f.icon;
          return (
            <div key={f.k} className="relative w-full min-w-0">
              <FI className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input data-testid={`search-field-${f.k}`} type={f.type || "text"} value={vals[f.k] || ""} list={listFor(f.k)}
                onChange={(e) => setVals((v) => ({ ...v, [f.k]: e.target.value }))} placeholder={f.ph}
                className="w-full min-w-0 rounded-lg border border-input bg-background py-3 pl-9 pr-3 text-sm outline-none transition-colors focus:border-primary" />
            </div>
          );
        }) : <p className="min-w-0 flex-1 py-3 text-sm text-muted-foreground">Join clubs, follow matches and share updates with the SPORA1 community.</p>}
        <Link to={to} data-testid="search-cta-btn"
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:bg-[#FF8533]">
          <Search className="h-4 w-4" /> {tab.cta}
        </Link>
      </div>
    </div>
  );
}

const QUICK = [
  { label: "Book a Facility", to: "/app/facilities", icon: Dumbbell },
  { label: "Create a Team", to: "/app/teams", icon: Users },
  { label: "Find a Match", to: "/app/matches", icon: Swords },
  { label: "Explore Trips", to: "/app/enquiries", icon: Map },
];

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0">
        <img src={IMG.hero} alt="Sports ground" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/80 via-foreground/55 to-foreground/25" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 pb-14 pt-20 sm:px-6 lg:pb-20 lg:pt-28">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 font-mono text-xs uppercase tracking-widest text-white backdrop-blur">
            One Ecosystem <span className="text-primary">•</span> One Technology <span className="text-primary">•</span> Many Champions
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Find grounds, build teams,<br /><span className="text-primary">play the game.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/85">
            Discover and book sports facilities, form teams, join matches and run institutional events & sports tourism - all in one place.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-8 min-w-0">
          <SearchWidget />
        </motion.div>

        <div className="mt-6 flex flex-wrap gap-2">
          {QUICK.map((q) => {
            const I = q.icon;
            return (
              <Link key={q.label} to={loginTo(q.to)} data-testid={`hero-quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20">
                <I className="h-4 w-4 text-primary" /> {q.label}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
